import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, interval } from 'rxjs';
import { map } from 'rxjs/operators';

export interface CountdownTime {
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CountdownTimerService {
  private countdownSubject = new BehaviorSubject<CountdownTime>({
    hours: 0,
    minutes: 0,
    seconds: 0,
    isExpired: true,
  });

  constructor() {}

  /**
   * Get countdown to next 12pm AEST deadline
   * Returns an observable that updates every second
   */
  getCountdownToDeadline(): Observable<CountdownTime> {
    return interval(1000).pipe(map(() => this.calculateTimeRemaining()));
  }

  /**
   * Calculate time remaining until next 12pm AEST deadline
   */
  private calculateTimeRemaining(): CountdownTime {
    const now = new Date();

    // Get the current date in AEST timezone
    // We'll use a more accurate approach by creating the deadline in AEST
    const today = new Date();

    // Create a date object for 12pm AEST today
    // We'll use the user's timezone and calculate the difference to AEST
    const userTimezoneOffset = today.getTimezoneOffset() * 60000; // Convert to milliseconds
    const aestOffset = 10 * 60 * 60000; // AEST is UTC+10 (in milliseconds)

    // Create deadline for today at 12pm AEST
    const deadlineAEST = new Date(today);
    deadlineAEST.setHours(12, 0, 0, 0);

    // Convert to UTC by adding user's timezone offset
    const deadlineUTC = deadlineAEST.getTime() + userTimezoneOffset;

    // Adjust for AEST timezone
    const deadlineInAEST = deadlineUTC + aestOffset;

    // If the deadline has passed today, set it to tomorrow
    const nowInAEST = now.getTime() + userTimezoneOffset + aestOffset;
    if (nowInAEST >= deadlineInAEST) {
      deadlineAEST.setDate(deadlineAEST.getDate() + 1);
      const newDeadlineUTC = deadlineAEST.getTime() + userTimezoneOffset;
      const newDeadlineInAEST = newDeadlineUTC + aestOffset;
      const timeRemaining = newDeadlineInAEST - nowInAEST;

      if (timeRemaining <= 0) {
        return {
          hours: 0,
          minutes: 0,
          seconds: 0,
          isExpired: true,
        };
      }

      const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
      const minutes = Math.floor(
        (timeRemaining % (1000 * 60 * 60)) / (1000 * 60)
      );
      const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

      return {
        hours,
        minutes,
        seconds,
        isExpired: false,
      };
    }

    const timeRemaining = deadlineInAEST - nowInAEST;

    if (timeRemaining <= 0) {
      return {
        hours: 0,
        minutes: 0,
        seconds: 0,
        isExpired: true,
      };
    }

    const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
    const minutes = Math.floor(
      (timeRemaining % (1000 * 60 * 60)) / (1000 * 60)
    );
    const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

    return {
      hours,
      minutes,
      seconds,
      isExpired: false,
    };
  }

  /**
   * Format countdown time as a string
   */
  formatCountdown(countdown: CountdownTime): string {
    if (countdown.isExpired) {
      return 'Deadline passed';
    }

    const hours = countdown.hours.toString().padStart(2, '0');
    const minutes = countdown.minutes.toString().padStart(2, '0');
    const seconds = countdown.seconds.toString().padStart(2, '0');

    return `${hours}:${minutes}:${seconds}`;
  }

  /**
   * Get formatted countdown string
   */
  getFormattedCountdown(): Observable<string> {
    return this.getCountdownToDeadline().pipe(
      map((countdown) => this.formatCountdown(countdown))
    );
  }
}
